/**
 * Utility functions for step validation in Copilot flow
 *
 * This module handles step completion validation for different step types:
 * - Connection/General steps: Use array of FieldConfig objects with required field validation
 * - Account/Access steps: Use single FieldConfig object with attributeValue validation
 */

import type { FieldConfig } from '../data-layer/types';

/**
 * Check if a step is completed based on field configurations
 *
 * Different step types have different data structures:
 * - Connection/General: fieldConfigs is FieldConfig[] (array)
 * - Account/Access: fieldConfigs is FieldConfig (single object)
 *
 * @param stepName - Name of the step (used to determine validation strategy)
 * @param stepFormData - Form data for the step (fallback for array-based validation)
 * @param fieldConfigs - Field configurations (array for Connection/General, object for others)
 * @returns boolean indicating if the step is completed
 */
const isStepCompleted = (
  stepName: string,
  stepFormData: Record<string, unknown>,
  fieldConfigs: FieldConfig[] | FieldConfig
): boolean => {
  const stepType = stepName.toLowerCase();

  // Handle single object fieldConfigs (account/access steps)
  // These steps receive a single FieldConfig object with attributeValue
  if (stepType.includes('account') || stepType.includes('access')) {
    return validateSingleFieldConfig(fieldConfigs as FieldConfig);
  }

  // Handle array fieldConfigs (connection/general steps)
  // These steps receive an array of FieldConfig objects with required field validation
  return validateArrayFieldConfigs(fieldConfigs as FieldConfig[], stepFormData);
};

/**
 * Validate single field configuration (for account/access steps)
 *
 * Account/Access steps receive fieldConfigs as a single object with structure:
 * {
 *   attributeName: "externalconnattvalue.CreateAccountJSON",
 *   attributeValue: "{...json...}",
 *   mandatory: "False",
 *   ...
 * }
 *
 * Validation simply checks if attributeValue exists and is not empty
 *
 * @param config - Single FieldConfig object
 * @returns boolean indicating if the field has a valid value
 */
const validateSingleFieldConfig = (config: FieldConfig): boolean => {
  // Check if attributeValue exists and is not null/undefined
  if (!config.attributeValue) {
    return false;
  }

  // For string values, ensure it's not empty after trimming whitespace
  if (typeof config.attributeValue === 'string') {
    const trimmedValue = config.attributeValue.trim();

    // If empty, it's skipped
    if (trimmedValue === '') {
      return false;
    }

    return true;
  }

  // For object values, ensure it's not an empty object
  if (typeof config.attributeValue === 'object') {
    return (
      Object.keys(config.attributeValue as Record<string, unknown>).length > 0
    );
  }

  return true;
};

/**
 * Validate array field configurations (for connection/general steps)
 *
 * Connection/General steps receive fieldConfigs as an array of objects:
 * [
 *   { attributeName: "externalconnattvalue.URL", required: true, attributeValue: "..." },
 *   { attributeName: "externalconnattvalue.USERNAME", required: true, attributeValue: "..." },
 *   ...
 * ]
 *
 * Validation checks all required fields and ensures they have valid values.
 * Uses attributeValue first (from API/parser), falls back to stepFormData (from user input).
 *
 * @param configs - Array of FieldConfig objects
 * @param stepFormData - Form data as fallback for field values
 * @returns boolean indicating if all required fields are completed
 */
const validateArrayFieldConfigs = (
  configs: FieldConfig[],
  stepFormData: Record<string, unknown>
): boolean => {
  // Ensure we have a valid array of configurations
  if (!Array.isArray(configs) || configs.length === 0) {
    return false;
  }

  // Filter to only required fields (those marked with required: true)
  const mandatoryFields = configs.filter((field: FieldConfig) => {
    if (field.attributeName.toLowerCase().includes('password')) {
      return false;
    }

    return field.required === true;
  });

  // If no mandatory fields exist, step cannot be completed
  if (mandatoryFields.length === 0) {
    return false;
  }

  // Check that all mandatory fields have valid values
  return mandatoryFields.every((field: FieldConfig) => {
    // Priority: attributeValue (from API/parser) > stepFormData (from user input)
    // Using nullish coalescing (??) to handle empty strings properly
    const value =
      field.attributeValue?.trim() || stepFormData[field.attributeName];

    // Reject null/undefined values
    if (value === null || value === undefined) {
      return false;
    }

    // For strings, ensure not empty after trimming whitespace
    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    // For arrays, ensure not empty
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // All other types are considered valid if not null/undefined
    return true;
  });
};

export default isStepCompleted;
